/**
 * StrategyHealthSuggestionCard — Renderer für 'strategy_health'-Suggestions
 * aus dem scheduled-strategy-health Watcher.
 *
 * Zeigt:
 *   - Signal-Badge (Reject-Rate / Error-Rate / Latenz-Drift / Cost-Spike)
 *   - Capability-Name (Code)
 *   - Aktueller Wert vs. Schwelle, plus Run-Count im Fenster
 *   - Rationale + Recommendation
 *   - Primärer Sprung: Operator-Konsole (Settings → KI-Companion)
 *
 * Patient-spezifisch ist diese Suggestion NICHT (patient_id ist null) —
 * sie betrifft die Plattform-Gesundheit, nicht eine klinische Maßnahme.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, ChevronRight, Clock, Wallet, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isStrategyHealthPayload,
  type AiSuggestion,
  type StrategyHealthSuggestionPayload,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

const SIGNAL_META: Record<
  StrategyHealthSuggestionPayload['signal'],
  { label: string; Icon: React.ElementType; color: string }
> = {
  reject_rate: {
    label: 'Reject-Rate',
    Icon: AlertTriangle,
    color: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
  },
  error_rate: {
    label: 'Error-Rate',
    Icon: AlertTriangle,
    color: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
  },
  latency_drift: {
    label: 'Latenz-Drift',
    Icon: Clock,
    color: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  },
  cost_spike: {
    label: 'Kosten-Spike',
    Icon: Wallet,
    color: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  },
};

export const StrategyHealthSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isStrategyHealthPayload(suggestion.payload)) return null;
  const p = suggestion.payload;
  const meta = SIGNAL_META[p.signal];

  const fmtValue = (v: number) => {
    if (p.signal === 'reject_rate' || p.signal === 'error_rate') {
      return `${(v * 100).toFixed(1).replace('.', ',')}%`;
    }
    if (p.signal === 'latency_drift') return `${Math.round(v)} ms`;
    if (p.signal === 'cost_spike') return `${v.toFixed(4)} €`;
    return String(v);
  };

  const handlePrimary = async () => {
    navigate('/settings?tab=KI-Companion');
    await resolve(suggestion, 'accepted', 'open_settings');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Activity className="h-4 w-4 text-slate-600" />
          <span className={cn('rounded-md px-2 py-0.5 text-xs font-medium', meta.color)}>
            <meta.Icon className="mr-1 inline h-3 w-3" />
            {meta.label}
          </span>
          <code className="text-xs text-muted-foreground">{p.capability_name}</code>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <dl className="mb-2 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Aktuell</dt>
        <dd className="font-medium text-foreground">{fmtValue(p.current_value)}</dd>
        <dt className="text-muted-foreground">Schwelle</dt>
        <dd className="text-foreground">{fmtValue(p.threshold)}</dd>
        <dt className="text-muted-foreground">Runs 24h</dt>
        <dd className="text-foreground tabular-nums">{p.total_runs_24h}</dd>
      </dl>

      <p className="mb-1 text-sm text-foreground">{p.rationale}</p>
      <p className="mb-3 text-sm text-muted-foreground italic">{p.recommendation}</p>

      <Button size="sm" onClick={handlePrimary}>
        Operator-Konsole öffnen
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </article>
  );
};
